import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Code2, Shield, Rocket, Headphones, Building, Sparkles } from "lucide-react";

const reasons = [
  {
    icon: Code2,
    title: "Custom-Built Solutions",
    description:
      "Every solution is tailored specifically to your business needs, not off-the-shelf software.",
  },
  {
    icon: Shield,
    title: "Secure & Scalable Architecture",
    description:
      "Enterprise-grade security with infrastructure that scales as your business grows.",
  },
  {
    icon: Rocket,
    title: "Fast Deployment",
    description:
      "Quick turnaround times without compromising on quality or functionality.",
  },
  {
    icon: Headphones,
    title: "Dedicated Technical Support",
    description:
      "24/7 support from our expert team to ensure your systems run smoothly.",
  },
  {
    icon: Building,
    title: "Industry-Specific Expertise",
    description:
      "Deep understanding of call center and BPO operations that informs every solution.",
  },
];

const checklistItems = [
  "100% Custom Development",
  "Agile Development Process",
  "Post-Launch Support Included",
  "Regular Updates & Maintenance",
  "Transparent Communication",
  "Competitive Pricing",
];

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="why-us"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="why-choose-us-section"
    >
      {/* Animated background decoration */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none"
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />

      {/* Floating particles */}
      <motion.div
        className="absolute top-20 left-20 w-4 h-4 bg-blue-500/30 rounded-full"
        animate={{ 
          y: [0, -30, 0],
          opacity: [0.3, 0.8, 0.3],
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-40 right-32 w-3 h-3 bg-purple-500/30 rounded-full"
        animate={{ 
          y: [0, 20, 0],
          x: [0, -10, 0],
        }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Reasons list */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.span 
              className="text-sm uppercase tracking-widest text-blue-400 font-medium mb-4 inline-flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
            >
              <Sparkles size={16} className="animate-pulse" />
              Why Choose Us
            </motion.span>
            
            <motion.h2 
              className="text-3xl md:text-5xl font-bold tracking-tight mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
            >
              The <span className="text-gradient">Mighty</span> Advantage
            </motion.h2>

            <div className="space-y-6">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="flex gap-4 group cursor-pointer"
                  data-testid={`reason-${index}`}
                >
                  <motion.div 
                    className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20 transition-colors"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <reason.icon className="text-blue-400 group-hover:text-blue-300 transition-colors" size={22} />
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                      {reason.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Visual card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <motion.div 
              className="glass-card p-8 md:p-10 relative overflow-hidden"
              whileHover={{ 
                boxShadow: "0 25px 50px rgba(59, 130, 246, 0.15)",
              }}
            >
              {/* Animated shine effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
              />

              {/* Header */}
              <motion.div 
                className="flex items-center gap-4 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
              >
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Check className="text-white" size={32} />
                </motion.div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    Trusted Partner
                  </h3>
                  <p className="text-slate-400">For Growing Businesses</p>
                </div>
              </motion.div>

              {/* Animated Checklist */}
              <div className="space-y-4">
                {checklistItems.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: 30 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.08 }}
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-3 group cursor-pointer"
                  >
                    <motion.div 
                      className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/30 transition-colors"
                      initial={{ scale: 0 }}
                      animate={isInView ? { scale: 1 } : {}}
                      transition={{ delay: 0.8 + index * 0.08, type: "spring" }}
                    >
                      <Check className="text-green-400" size={14} />
                    </motion.div>
                    <span className="text-slate-300 group-hover:text-white transition-colors">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom stats with animated counters */}
              <motion.div 
                className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 gap-6"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 1 }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="cursor-pointer"
                >
                  <motion.div 
                    className="text-3xl font-bold text-gradient"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.1 }}
                  >
                    98%
                  </motion.div>
                  <div className="text-sm text-slate-400">On-Time Delivery</div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="cursor-pointer"
                >
                  <motion.div 
                    className="text-3xl font-bold text-gradient"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.2 }}
                  >
                    4.9/5
                  </motion.div>
                  <div className="text-sm text-slate-400">Client Rating</div>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Decorative floating elements */}
            <motion.div 
              className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <motion.div 
              className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{ duration: 5, repeat: Infinity, delay: 1 }}
            />
            
            {/* Rotating border decoration */}
            <motion.div
              className="absolute -top-2 -right-2 w-20 h-20 border-2 border-blue-500/30 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
